import { supabase } from '@/integrations/supabase/client';
import { POINT_VALUES, TIER_CONFIG, TIER_ORDER, type TierType } from '@/types/engagement';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Make sure the request carries the signed-in user's token. On mobile the
 * session can still be restoring when the dashboard mounts; queries sent
 * without the token silently return zero rows (showing 0 pts / no account).
 */
export async function ensureSession(maxWaitMs = 4000): Promise<boolean> {
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    const { data } = await supabase.auth.getSession();
    if (data.session?.access_token) return true;
    await sleep(400);
  }
  return false;
}

/** Fetch the user's level row, retrying when the row appears missing. */
export async function fetchUserLevelRow(userId: string) {
  await ensureSession();
  for (let attempt = 1; attempt <= 3; attempt++) {
    const { data, error } = await supabase
      .from('user_levels')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (!error && data) return data;
    if (attempt < 3) await sleep(1200 * attempt);
  }
  // Truly missing: create it (sanitized server-side to zero values).
  const { data: created } = await supabase
    .from('user_levels')
    .insert({ user_id: userId })
    .select()
    .maybeSingle();
  if (created) return created;
  const { data: again } = await supabase
    .from('user_levels')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  return again ?? null;
}

/** Number of messages the user has published (sent). */
export async function fetchPublishedCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('announcements')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('status', 'approved');
  if (!error && typeof count === 'number') return count;
  // Fallback: owner-scoped dashboard RPC
  const { data } = await supabase.rpc('get_my_announcements_for_dashboard' as never);
  if (Array.isArray(data)) {
    return (data as { status?: string }[]).filter((a) => a.status === 'approved').length;
  }
  return 0;
}

/** Total points = points from earned milestones + points for every message sent. */
export function combinePoints(storedPoints: number, publishedCount: number): number {
  return (storedPoints || 0) + publishedCount * POINT_VALUES.publish_announcement;
}

export function tierForPoints(points: number): TierType {
  let tier: TierType = 'awareness';
  for (const t of TIER_ORDER) {
    if (points >= TIER_CONFIG[t].minPoints) tier = t;
  }
  return tier;
}
